import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import ScanTicket from "@/pages/ScanTicket";
import Tickets from "@/pages/Tickets";
import TicketDetail from "@/pages/TicketDetail";
import GenerateTickets from "@/pages/GenerateTickets";
import AddTicket from "@/pages/AddTicket";
import RedemptionHistory from "@/pages/RedemptionHistory";
import Purchases from "@/pages/Purchases";
import Reports from "@/pages/Reports";
import ActivityLog from "@/pages/ActivityLog";
import EventSettings from "@/pages/EventSettings";
import Roles from "@/pages/Roles";
import AdminVoting from "@/pages/AdminVoting";
import PublicVoting from "@/pages/PublicVoting";
import PrintTickets from "@/pages/PrintTickets";
import CheckIn from "@/pages/CheckIn";
import GuestRecharge from "@/pages/GuestRecharge";
import RechargeQR from "@/pages/RechargeQR";
import EventPortal from "@/pages/EventPortal";
import GuestGate from "@/components/GuestGate";
import { Toaster } from "@/components/ui/sonner";

function Shell({ children, roles }) {
  return (
    <ProtectedRoute roles={roles}>
      <Layout>{children}</Layout>
    </ProtectedRoute>
  );
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "bar" || user.role === "tickets") return <Navigate to="/scan" replace />;
  if (user.role === "registration" || user.role === "reception") return <Navigate to="/tickets" replace />;
  return <Navigate to="/dashboard" replace />;
}

function RouterBody() {
  const location = useLocation();
  if (location.hash && location.hash.includes("session_id=")) return <AuthCallback />;
  const ADMIN = ["admin"];
  const ADMIN_REC = ["admin", "registration", "reception"];
  const ADMIN_TIX = ["admin", "bar", "tickets"];
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/vote" element={<GuestGate><PublicVoting /></GuestGate>} />
      <Route path="/event" element={<GuestGate><EventPortal /></GuestGate>} />
      <Route path="/events" element={<GuestGate><EventPortal /></GuestGate>} />
      <Route path="/recharge" element={<GuestGate><GuestRecharge /></GuestGate>} />
      <Route path="/recharge-qr" element={<Shell roles={ADMIN}><RechargeQR /></Shell>} />
      <Route path="/" element={<RootRedirect />} />
      <Route path="/dashboard" element={<Shell roles={ADMIN}><Dashboard /></Shell>} />
      <Route path="/scan" element={<Shell roles={ADMIN_TIX}><ScanTicket /></Shell>} />
      <Route path="/check-in" element={<Shell roles={ADMIN_REC}><CheckIn /></Shell>} />
      <Route path="/tickets" element={<Shell roles={ADMIN_REC}><Tickets /></Shell>} />
      <Route path="/tickets/:id" element={<Shell roles={ADMIN_REC}><TicketDetail /></Shell>} />
      <Route path="/generate" element={<Shell roles={ADMIN}><GenerateTickets /></Shell>} />
      <Route path="/add" element={<Shell roles={ADMIN}><AddTicket /></Shell>} />
      <Route path="/print" element={<Shell roles={ADMIN}><PrintTickets /></Shell>} />
      <Route path="/history" element={<Shell roles={ADMIN}><RedemptionHistory /></Shell>} />
      <Route path="/purchases" element={<Shell roles={ADMIN_REC}><Purchases /></Shell>} />
      <Route path="/voting" element={<Shell roles={ADMIN}><AdminVoting /></Shell>} />
      <Route path="/reports" element={<Shell roles={ADMIN}><Reports /></Shell>} />
      <Route path="/roles" element={<Shell roles={ADMIN}><Roles /></Shell>} />
      <Route path="/activity" element={<Shell roles={ADMIN}><ActivityLog /></Shell>} />
      <Route path="/settings" element={<Shell roles={ADMIN}><EventSettings /></Shell>} />
      <Route path="*" element={<RootRedirect />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <RouterBody />
      </BrowserRouter>
      <Toaster richColors position="top-right" />
    </AuthProvider>
  );
}
